/* eslint-disable no-param-reassign */
/* eslint-disable no-console */
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import { makeStyles, useTheme } from '@material-ui/core';
import { ChartBulletShape, chartDefaultColors, chartDefaultCursorStroke, chartDefaultGridStroke, chartFullDateFormat, ChartSeriesStyle, ChartSettings,
    getChartSeriesStyle, ModeTheme, XYChartType, ChartColorSettings, MultiMetricsDataPoint } from '@moderepo/mode-app-components';
import clsx from 'clsx';
import React, { CSSProperties, useCallback, useEffect, useMemo, useRef } from 'react';
import { BasicXYChart } from '../../utils/constants';
import { getDataResolution } from './tsUtils';



const TAG_CHART_BAR_HEIGHT = 30;


/**
 * An interface to define metric for the chart.
 */
export interface ChartMetricInfo {
    // Unique ID that will be used to find the metric' data from the chart's data object
    readonly id: string;
    // Non unique metric name
    readonly name: string;
    readonly displayName: string;
    readonly decimalPlaces?: number | undefined;
    readonly unit?: string;
    readonly range?: {
        readonly min?: number | undefined;
        readonly max?: number | undefined;
    },
    readonly enumDefinitions?: readonly {
        readonly name: string;
        readonly value: string;
    }[] | undefined;
    readonly color?: string | undefined;
    readonly contrastTextColor?: string | undefined;
    readonly chartType?: BasicXYChart | undefined;

    // The group name of the Y Axis for sharing Y axis. For example, if we want 2 metrics to use the same Y Axis, we give them
    // the same yAxisGroupName.
    readonly yAxisGroupName?: string | undefined;
}


/**
 * Interface for 1 data point for GanttChart. Each data point contains all the information needed to create 1 bar in gantt chart
 */
export interface GanttChartDataPoint {
    metricId: string;
    displayName: string;
    startDate: number;
    endDate: number;
    color?: string;
    value: string | undefined;
}


/**
 * Extended ChartSettings. We need additional settings for the chart.
 */
export interface ExtChartSettings extends ChartSettings {
    readonly getNumericMetricChartYAxisHTMLLabel?: (
        (text: string | undefined, targetAxis: am4charts.AxisLabel, targetMetric: ChartMetricInfo)=> string | undefined
    ) | undefined;
    readonly getNumericMetricChartYAxisTextLabel?: (
        (text: string | undefined, targetAxis: am4charts.AxisLabel, targetMetric: ChartMetricInfo)=> string | undefined
    ) | undefined;
    readonly getTagMetricChartYAxisHTMLLabel?: (
        (text: string | undefined, targetAxis: am4charts.AxisLabel, targetMetric: ChartMetricInfo)=> string | undefined
    ) | undefined;
    readonly getTagMetricChartYAxisTextLabel?: (
        (text: string | undefined, targetAxis: am4charts.AxisLabel, targetMetric: ChartMetricInfo)=> string | undefined
    ) | undefined;
    readonly getNumericMetricChartTooltipText?: (
        (text: string | undefined, targetSeries: am4charts.XYSeries, targetMetric: ChartMetricInfo)=> string | undefined
    ) | undefined;
    readonly getNumericMetricChartTooltipHTML?: (
        (text: string | undefined, targetSeries: am4charts.XYSeries, targetMetric: ChartMetricInfo)=> string | undefined
    ) | undefined;
    readonly getTagMetricChartTooltipText?: (
        (text: string | undefined, targetSeries: am4charts.XYSeries, targetMetric: ChartMetricInfo)=> string | undefined
    ) | undefined;
    readonly getTagMetricChartTooltipHTML?: (
        (text: string | undefined, targetSeries: am4charts.XYSeries, targetMetric: ChartMetricInfo)=> string | undefined
    ) | undefined;
}


export interface DrawBox {
    readonly stroke?: {
        readonly width?: number | undefined;
        readonly color?: string | undefined;
        readonly opacity?: number | undefined;
        readonly dasharray?: string | undefined;
    } | undefined;

    // Fill settings. Only needed if there is an "endValue" settings
    readonly fill?: {
        readonly color?: string | undefined;
        readonly opacity?: number | undefined;
    } | undefined;

    readonly label?: {
        readonly text: string;
        readonly color?: string | undefined;
    } | undefined;
}

/**
 * To draw a box vertically along the X Axis
 */
export interface XAxisBox extends DrawBox {
    readonly value: string;
    // endValue is optional. If not provided, a line will be displayed.
    readonly endValue?: string | undefined;
}

/**
 * To draw a box horizontally along the Y Axis
 */
export interface YAxisBox extends DrawBox {
    readonly value: number;
    // endValue is optional. If not provided, a line will be displayed.
    readonly endValue?: number | undefined;
}


export interface ChartViewSettings {
    readonly numericChartSettings?: {
        readonly chartType?: BasicXYChart | undefined;
        readonly shareYAxis?: boolean | undefined;

        // Whether or not to sync the Y axes so that the Y-Axes' labels are aligned. The default is TRUE unless this is specifically set to
        // false. NOTE: If this is true, the label and grid line will be aligned however, the Y-Axes' range might not match the settings. Amcharts
        // have it's own adjust the range accordingly. To have Amchart use the Y-Axis' range settings, set this to false. HOWEVER, this will
        // cause the chart's label and grid line out of sync.
        readonly syncYAxes?: boolean | undefined;

        readonly yAxisRange?: {
            readonly min?: number | undefined;
            readonly max?: number | undefined;
        } | undefined;
        readonly groupYAxesBy?: 'unit' | 'group_name' | undefined;
        readonly yAxisRangeByGroupName?: {
            readonly [groupName: string]: {
                readonly min?: number | undefined;
                readonly max?: number | undefined;
            }
        } | undefined;
        readonly selectedMetricStyle?: {
            // style to apply to series
            readonly width: number | undefined;
        } | undefined;
        readonly cursorBehavior?: am4charts.XYCursor['behavior'] | undefined;
        readonly showVerticalControlBar?: boolean | undefined;
        readonly showHorizontalControlBar?: boolean | undefined;

        // To add vertical/horizontal lines/boxes on the charts
        readonly xAxisBoxes?: readonly XAxisBox[] | undefined;
        readonly yAxisBoxes?: readonly YAxisBox[] | undefined;

        // Whether to connect the data that has gap in between. The default is TRUE for compatibility reason and the chart will connect 2 data points
        // if there is a gap in the middle. This option will work with only aggregated time-series data like average or sum.
        // Because, not to connect the gap, the record interval in data must be constant like every 5 seconds.
        readonly connectDataGap?: boolean;
    } | undefined;
    readonly tagChartSettings?: {
        readonly cursorBehavior?: am4charts.XYCursor['behavior'] | undefined;
        readonly showHorizontalControlBar?: boolean | undefined;
    } | undefined;
    readonly verticalLineStyle?: {
        readonly color?: string | undefined;
        readonly width?: number | undefined;
        readonly opacity?: number | undefined;
        readonly strokeDasharray?: string | undefined;
    } | undefined;
}


const useStyle = makeStyles(() => {
    return {
        root: {
            width: '100%',
        },

        chart: {
            display   : 'block',
            width     : '100%',
            '&.hidden': {
                display: 'none',
            },
        },

        numericChart: {
            height: 500,
        },
    };
}, { name: 'SynchronizedNumericTagMetricsDisplayContainer', index: 1 });


/**
 * Given a MetricInfo object, create an ID we can use to assign to Y-Axis object. We will use the MetricInfo's ID + "yaxis" to create
 * unique ID for the Y-Axis
 */
const getYAxisId = (metricInfo: ChartMetricInfo): string => {
    return `yaxis-${metricInfo.id}`;
};



/**
 * Create a Bullet for the chart
 */
const createChartBullet = (seriesStyle: ChartSeriesStyle): am4charts.Bullet => {

    const interfaceColors = new am4core.InterfaceColorSet();

    const bullet = new am4charts.Bullet();
    bullet.width = seriesStyle.bullet.size;
    bullet.height = seriesStyle.bullet.size;

    const bulletShape = (() => {
        switch (seriesStyle.bullet.shape) {
            case ChartBulletShape.TRIANGLE: {
                const bulletShape = bullet.createChild(am4core.Triangle);
                bullet.horizontalCenter = 'middle';
                bullet.verticalCenter = 'middle';
                return bulletShape;
            }

            case ChartBulletShape.RECTANGLE: {
                const bulletShape = bullet.createChild(am4core.Rectangle);
                bullet.horizontalCenter = 'middle';
                bullet.verticalCenter = 'middle';
                return bulletShape;
            }

            case ChartBulletShape.CIRCLE:
            default: {
                return bullet.createChild(am4core.Circle);
            }
        }
    })();

    bulletShape.stroke = interfaceColors.getFor('background');
    bulletShape.fill = seriesStyle.color;
    bulletShape.strokeWidth = 1;
    bulletShape.width = seriesStyle.bullet.size;
    bulletShape.height = seriesStyle.bullet.size;

    return bullet;
};


/**
 * Create a Line and add it to the chart's plotArea. Customize the line based on the provided lineStyle and theme.
 */
const createVerticalLine = (
    chart: am4charts.XYChart,
    existingLine: am4core.Line | undefined,
    lineStyle: ChartViewSettings['verticalLineStyle'] | undefined,
    theme: ModeTheme,
): am4core.Line => {
    // Create a new line if an existingLine does not exist.
    const line = existingLine || chart.plotContainer.createChild(am4core.Line);
    line.zIndex = Number.MAX_SAFE_INTEGER;
    line.stroke = am4core.color(lineStyle?.color ?? theme.palette.text.primary);
    line.strokeWidth = lineStyle?.width ?? 2;
    line.strokeOpacity = lineStyle?.opacity ?? 0.5;
    line.strokeDasharray = lineStyle?.strokeDasharray ?? '';
    line.y1 = 0;
    line.y2 = chart.plotContainer.measuredHeight;
    return line;
};


/**
 * Create a new Chart and clean up existing chart if there is one
 */
const createChart = (
    existingChart: am4charts.XYChart | undefined,
    chartId: string,
    theme: ModeTheme,
    chartCompColors: {
        gridStroke: am4core.Color,
        cursorStroke: am4core.Color
    },
    cursorBehavior: am4charts.XYCursor['behavior'] | undefined,
) => {
    if (existingChart && !existingChart.isDisposed) {
        // Dispose old chart first
        // console.log('Dispose existing chart');

        // unsubscribe to data axis's events
        for (let i = 0; i < existingChart.xAxes.length; i += 1) {
            (existingChart.xAxes.getIndex(i) as am4charts.DateAxis).events.off('selectionextremeschanged');
        }

        existingChart.events.off('ready');
        existingChart.plotContainer.events.off('hit');

        // dispose chart
        existingChart.dispose();
    }


    // create new Chart
    // console.log('create chart', chartId);

    const chart = am4core.create(chartId, am4charts.XYChart);
    chart.dateFormatter.dateFormat = chartFullDateFormat;
    chart.plotContainer.background.stroke = chartCompColors.gridStroke;


    // add cursor
    chart.cursor = new am4charts.XYCursor();
    chart.cursor.lineY.disabled = true;
    chart.cursor.behavior = cursorBehavior ?? 'none';
    chart.cursor.opacity = 1;
    chart.cursor.lineX.stroke = chartCompColors.cursorStroke;


    // Create X Axis for the chart
    const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
    dateAxis.renderer.grid.template.strokeOpacity = 1;
    dateAxis.renderer.grid.template.strokeWidth = 1;
    dateAxis.renderer.grid.template.stroke = chartCompColors.gridStroke;
    dateAxis.renderer.labels.template.fill = am4core.color(theme.palette.text.primary);
    dateAxis.renderer.minGridDistance = 100;
    dateAxis.tooltipDateFormat = chartFullDateFormat;
    dateAxis.strictMinMax = true;

    return chart;
};


/**
 * Create a new Numeric Chart and clean up existing chart if there is one
 */
const createNumericChart = (
    existingChart: am4charts.XYChart | undefined,
    chartId: string,
    theme: ModeTheme,
    chartCompColors: {
        gridStroke: am4core.Color,
        cursorStroke: am4core.Color
    },
    cursorBehavior: am4charts.XYCursor['behavior'] | undefined,
) => {
    const chart = createChart(existingChart, chartId, theme, chartCompColors, cursorBehavior);
    return chart;
};


/**
 * Create a new Gantt Chart and clean up existing chart if there is one
 */
const createGanttChart = (
    existingChart: am4charts.XYChart | undefined,
    chartId: string,
    theme: ModeTheme,
    chartCompColors: {
        gridStroke: am4core.Color,
        cursorStroke: am4core.Color
    },
    cursorBehavior: am4charts.XYCursor['behavior'] | undefined,
) => {
    const chart = createChart(existingChart, chartId, theme, chartCompColors, cursorBehavior);
    chart.leftAxesContainer.layout = 'vertical';
    chart.rightAxesContainer.layout = 'vertical';

    // Create X Axis for the chart
    const dateAxis = chart.xAxes.getIndex(0) as am4charts.DateAxis;
    if (dateAxis) {
        dateAxis.baseInterval = { count: 1, timeUnit: 'second' };
    }

    return chart;
};



/**
 * Helper function to set up chart's export menu
 * @param chart
 * @param exportSettings
 */
const setUpChartExportSettings = (chart: am4charts.XYChart, exportSettings: ChartSettings['exportSettings']) => {
    if (exportSettings) {
        if (!chart.exporting.menu) {
            chart.exporting.menu = new am4core.ExportMenu();
        }
        if (exportSettings.align) {
            chart.exporting.menu.align = exportSettings.align;
        }
        if (exportSettings.verticalAlign) {
            chart.exporting.menu.verticalAlign = exportSettings.verticalAlign;
        }
        if (exportSettings.items) {
            chart.exporting.menu.items = exportSettings.items;
        }
    } else if (!exportSettings && chart.exporting.menu) {
        if (chart.exporting.menu) {
            chart.exporting.menu.dispose();
        }
    }
};



/**
 * Helper function to get the tooltip text for numeric chart.
 * @param displayName
 * @param unit
 * @returns
 */
const getNumericChartTooltipFormat = (displayName: string | undefined, unit: string | undefined) => {
    if (displayName) {
        return `${displayName}: [bold]{valueY}[/]${unit ? ` ${unit}` : ''}`;
    }
    return `[bold]{valueY}[/]${unit ? ` ${unit}` : ''}`;
};


/**
 * Helper function to return the tooltip text format for tag metric bar
 */
const getGanttChartTooltipFormat = (
    texts: {
        readonly fromLabel: string;
        readonly toLabel: string;
        readonly valueLabel: String;
    },
) => {
    return `[bold]{displayName}[/]
            [bold]${texts.fromLabel}[/]: {openDateX}
            [bold]${texts.toLabel}[/]: {dateX}
            [bold]${texts.valueLabel}[/]: {value}`;
};



/**
 * Create a new Y Axis and Series for numeric metric and add it to the chart
 */
const createNumericMetricSeries = (
    chart: am4charts.XYChart,
    dateAxis: am4charts.DateAxis,
    yAxesByGroupName: {
        [groupName: string]: am4charts.ValueAxis | undefined;
    },
    metric: ChartMetricInfo,
    highlightMetric: boolean,
    seriesIndex: number,
    totalSeriesCount: number,
    chartViewSettings: ChartViewSettings | undefined,
    theme: ModeTheme,
    chartStyles: {
        chartColors: readonly ChartColorSettings[],
        gridStroke: am4core.Color,
        cursorStroke: am4core.Color,
    },
    onNumericMetricSeriesClicked: ((metricId: string, date: number)=> void) | undefined,
) => {

    // deconstruct the values from "chartViewSettings?.numericChartSettings" so that we don't need to keep typing
    // "chartViewSettings?.numericChartSettings.chartType" or "chartViewSettings?.numericChartSettings.shareYAxis" which are too long.
    const {
        shareYAxis, yAxisRange, groupYAxesBy, yAxisRangeByGroupName, syncYAxes, selectedMetricStyle, connectDataGap,
    } = chartViewSettings?.numericChartSettings ?? {
        shareYAxis           : undefined,
        yAxisRange           : undefined,
        groupYAxesBy         : undefined,
        yAxisRangeByGroupName: undefined,
        syncYAxes            : undefined,
        selectedMetricStyle  : undefined,
        connectDataGap       : undefined,
    };

    // Use the specific chartType configured for the metric or fallback to use the chartType configured for the the entire chart
    const chartType = metric.chartType ?? chartViewSettings?.numericChartSettings?.chartType;

    // Pick a random series color and bullet style
    // If metric contains custom color settings then only use that color. If not, use the set of chartColors defined in the theme
    const seriesStyle = getChartSeriesStyle(
        metric.color ? [{
            color       : am4core.color(metric.color),
            contrastText: metric.contrastTextColor ? am4core.color(metric.contrastTextColor) : undefined,
        }] : chartStyles.chartColors,
        totalSeriesCount > 0 ? seriesIndex : 0,
    );


    // Create Y Axis
    const valueAxis = (() => {
        // Get the FIRST Y-Axis in the chart
        const yAxis = chart.yAxes.getIndex(0) as am4charts.ValueAxis;

        // If shareYAxis is turned on AND there is already 1 Y-Axis created then return that Y-Axis, we don't need to create any more Y-Axis
        if (shareYAxis && yAxis) {
            return yAxis;
        }

        // If "groupYAxesBy" is not configured then create a new Y-Axis, there is no sharing Y-Axes so we create 1 Y-Axis for each metric
        if (groupYAxesBy === undefined) {
            const newYAxis = chart.yAxes.push(new am4charts.ValueAxis());
            newYAxis.id = getYAxisId(metric);
            return newYAxis;
        }

        // If "groupYAxesBy" is configured then we will check if there is already a Y-Axis created for the metric's group. If there is, use
        // that Y-Axis. If not then we create a new Y-Axis.

        // First find the groupName. If "groupYAxesBy" is "group_name" then use the "yAxisGroupName" set in the metric settings. If "groupYAxesBy"
        // is "unit" then use the metric's unit as groupName.
        const groupName = groupYAxesBy === 'group_name' ? metric.yAxisGroupName : metric.unit;

        // Find the Y-Axis created for the specified groupName. If we find one, return that Y-Axis
        const existingYAxis = groupName ? yAxesByGroupName[groupName] : undefined;
        if (existingYAxis) {
            return existingYAxis;
        }

        // If a Y-Axis has not been created for the specified groupName, create a new Y-Axis
        const newYAxis = chart.yAxes.push(new am4charts.ValueAxis());
        newYAxis.id = getYAxisId(metric);
        if (groupName !== undefined) {
            // Remember the Y-Axis for the groupName. The next metric that has the same groupName will use this Y-Axis
            yAxesByGroupName[groupName] = newYAxis;
        }
        return newYAxis;
    })();

    if (!shareYAxis && valueAxis !== chart.yAxes.getIndex(0) && syncYAxes !== false) {
        // If we are NOT sharing Y axes and if the valueAxis is NOT the first axis, sync it with the FIRST Y axis.
        // This will align all the Y axes' grid lines.
        valueAxis.syncWithAxis = chart.yAxes.getIndex(0) as am4charts.ValueAxis;
    }

    if (valueAxis.tooltip) {
        valueAxis.tooltip.disabled = true;
    }

    const range = (() => {
        // If "numericChartSettings.yAxisRange" is provided, always use this range. This range takes precedence over all other settings.
        // Of if shareYAxis is set to true also use "numericChartSettings.yAxisRange" even if it is undefined. Because if "shareYAxis" is
        // turned on, there will be only 1 Y-Axis so we have to use this "numericChartSettings.yAxisRange" settings.
        if (yAxisRange || shareYAxis) {
            return yAxisRange;
        }

        // If "groupYAxesBy" is set to some value, that means some of the metrics will share the same Y-Axis. For this case, we can't use the
        // individual metric's "range" settings. If metric A and B share the same Y Axis but metric A has range=0 to 10 and metric B has
        // range=0 to 100, which range do we use? Therefore, we will use the numericChartSettings.yAxisRangeByGroupName settings
        if (groupYAxesBy !== undefined) {
            const groupName = groupYAxesBy === 'group_name' ? metric.yAxisGroupName : metric.unit;
            if (groupName) {
                return yAxisRangeByGroupName?.[groupName];
            }
            return undefined;
        }

        // For other cases, we can use the individual metric's range settings.
        return metric.range;
    })();


    if (range?.min !== undefined) {
        valueAxis.min = range.min;
    }
    if (range?.max !== undefined) {
        valueAxis.max = range.max;
    }

    // If one of the min/max is provided then set strictMinMax to true
    if (range?.min !== undefined || range?.max !== undefined) {
        valueAxis.strictMinMax = true;
    }

    // Amcharts numberformat syntax is like this
    // "#.##" To round numbers to 2 decimal places
    // "#.####" To round numbers to 4 decimal places
    valueAxis.numberFormatter = new am4core.NumberFormatter();
    valueAxis.numberFormatter.numberFormat = `#.${'#'.repeat(metric.decimalPlaces ?? 3)}`;

    valueAxis.height = am4core.percent(100);
    valueAxis.align = 'right';
    valueAxis.contentAlign = 'right';
    valueAxis.paddingRight = 10;


    // Style the valueAxis vertical line, make it the same as the series style
    valueAxis.renderer.line.strokeOpacity = 1;
    valueAxis.renderer.line.strokeWidth = 2;
    if (shareYAxis) {
        // If all the series share the same Y axis, we don't want to use the series' color for the Y axis color. We will use the the text color
        valueAxis.renderer.line.stroke = am4core.color(theme.palette.text.primary);
    } else {
        valueAxis.renderer.line.stroke = seriesStyle.color;
    }
    valueAxis.renderer.labels.template.fill = am4core.color(theme.palette.text.primary);
    valueAxis.renderer.grid.template.strokeOpacity = 1;
    valueAxis.renderer.grid.template.strokeWidth = 1;
    valueAxis.renderer.grid.template.stroke = am4core.color(chartStyles.gridStroke);


    // Create a new series
    const series = (() => {
        if (chartType === XYChartType.BAR || chartType === XYChartType.STACKED_BAR) {
            const series = chart.series.push(new am4charts.ColumnSeries());
            series.stacked = chartType === XYChartType.STACKED_BAR;
            series.columns.template.width = am4core.percent(80);
            return series;
        }
        return chart.series.push(new am4charts.LineSeries());
    })();
    series.zIndex = totalSeriesCount - seriesIndex;
    series.dataFields.dateX = 'dataPointTimestamp';
    series.dataFields.valueY = metric.id;
    series.yAxis = valueAxis;
    series.xAxis = dateAxis;
    series.id = metric.id;                   // Give the series the same ID as the metric so that we can look up metric by series.id
    series.name = metric.displayName;        // This is used by the chart legend
    series.showOnInit = false;


    // Synchronize the tooltip color with the series' color
    if (series.tooltip) {
        series.tooltip.numberFormatter = valueAxis.numberFormatter;
        series.tooltip.getFillFromObject = false;
        series.tooltip.background.fill = seriesStyle.color;
        series.tooltip.strokeWidth = 0;
        if (seriesStyle.contrastText) {
            series.tooltip.label.fill = seriesStyle.contrastText;
        }
    }

    if (series instanceof am4charts.LineSeries) {
        series.tensionX = 1;
        series.connect = connectDataGap ?? true;
    }

    // Set up stoke style
    if (chartType === XYChartType.SCATTER) {
        series.strokeWidth = 0; // don't draw the line between the scatter points
        series.bullets.push(createChartBullet(seriesStyle));
    } else if (
        chartType === XYChartType.BAR || chartType === XYChartType.STACKED_BAR
    ) {
        series.stroke = seriesStyle.color;
        series.strokeWidth = 2;
        series.strokeOpacity = 1;
    } else {
        series.stroke = seriesStyle.color;
        // Draw a thicker line when the series is highlighted
        series.strokeWidth = highlightMetric ? selectedMetricStyle?.width ?? 5 : 2;
        series.strokeOpacity = 1;
    }

    // Set up fill style
    if (chartType === XYChartType.AREA) {
        series.fill = seriesStyle.color;
        series.fillOpacity = 0.1;
    }
    if (chartType === XYChartType.BAR || chartType === XYChartType.STACKED_BAR) {
        series.fill = seriesStyle.color;
        series.fillOpacity = 0.8;
    }

    if (onNumericMetricSeriesClicked) {
        const onClickHandler = (event) => {
            const selectedSeries = series;
            const item = event.target.dataItem?.component?.tooltipDataItem.dataContext as MultiMetricsDataPoint;
            const metricId = selectedSeries.dataFields.valueY;

            if (item && metricId) {
                onNumericMetricSeriesClicked(metricId, item.dataPointTimestamp);
            }
        };

        if (series instanceof am4charts.LineSeries) {
            // If series is LineSeries, it can be either a SCATTER plot or Line/Area plot. For Scatter Plot, we need to listen to the click event
            // on the bullet
            if (chartType === XYChartType.SCATTER) {
                const bullet = series.bullets.getIndex(0);
                if (bullet) {
                    bullet.events.on('hit', onClickHandler);
                }
            } else {
                // For Line/Area plot, we listen to the click event on the Line segment
                const segment = series.segments.template;
                segment.interactionsEnabled = true;

                const hoverState = segment.states.create('hover');
                hoverState.properties.strokeWidth = 10;
                segment.events.on('hit', onClickHandler);
            }
        } else if (series instanceof am4charts.ColumnSeries) {
            // If series is ColumnSeries, it must be a BAR/STACKED_BAR chart. We need to listen to the click event on the columns.
            series.columns.template.events.on('hit', onClickHandler);
        }
    }
};


/**
 * Create 1 axis and 1 series for a Tag metric.
 */
const createGanttChartSeries = (
    chart: am4charts.XYChart,
    metric: ChartMetricInfo,
    theme: ModeTheme,
) => {
    const yAxis = chart.yAxes.push(new am4charts.CategoryAxis());
    yAxis.height = am4core.percent(100);
    yAxis.dataFields.category = 'metricId';


    // By default, amchart will display the tag metric from bottom to top. The tag metric that is defined first will be displayed at the bottom.
    // We want to show them in the reverse order.
    yAxis.renderer.inversed = true;
    yAxis.renderer.labels.template.fill = am4core.color(theme.palette.text.primary);
    yAxis.align = 'right';
    yAxis.contentAlign = 'right';

    yAxis.renderer.minGridDistance = TAG_CHART_BAR_HEIGHT;
    yAxis.renderer.grid.template.location = 0;


    yAxis.renderer.grid.template.stroke = am4core.color(theme.palette.text.primary);
    if (yAxis.tooltip) {
        yAxis.tooltip.disabled = true;
    }


    const series = chart.series.push(new am4charts.ColumnSeries());
    series.name = metric.displayName;        // This is used by the chart legend
    series.id = metric.id;                   // Give the series the same ID as the metric so that we can look up metric by series.id
    series.yAxis = yAxis;
    series.dataFields.openDateX = 'startDate';
    series.dataFields.dateX = 'endDate';
    series.dataFields.categoryY = 'metricId';
    series.columns.template.propertyFields.fill = 'color';      // get color from data point object. The field is 'color'
    series.columns.template.propertyFields.stroke = 'color';
    series.columns.template.strokeOpacity = 1;
};



/**
 * Interface for the props required by the SynchronizedNumericTagMetricsDisplayContainer
 */
export interface SynchronizedNumericTagMetricsDisplayContainerProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly numericChartStyle?: CSSProperties | undefined;
    readonly tagChartStyle?: CSSProperties | undefined;

    readonly chartId: string;

    // The set of metrics we want to show in the chart.
    readonly numericMetrics: readonly ChartMetricInfo[];
    readonly tagMetrics: readonly ChartMetricInfo[];


    // To control the metrics we want to show/hide. We want to separate this from the other numericMetrics and tagMetrics props because the
    // numericMetrics and tagMetrics props' changes will trigger to chart to be recreated. This "disabledNumericMetrics" will only trigger the
    // chart's series to be shown/hidden which amchart can do very quick
    readonly disabledNumericMetrics?: {
        readonly [metricId: string]: boolean | undefined;
    } | undefined;

    readonly disabledTagMetrics?: {
        readonly [metricId: string]: boolean | undefined;
    } | undefined;

    readonly chartSettings: ExtChartSettings;
    readonly chartViewSettings?: ChartViewSettings | undefined;

    // The actual data for the chart
    readonly metricsData: {
        readonly metricsDataPoints: MultiMetricsDataPoint[];
        readonly ganttChartDataPoints: {
            [metricId: string]: GanttChartDataPoint[] | undefined;
        }
    }

    readonly texts: {
        readonly fromLabel: string;
        readonly toLabel: string;
        readonly valueLabel: String;
    },

    readonly dateRange: {
        readonly start: number;
        readonly end: number;
    }

    readonly showVerticalLineAtTimestamp?: number | undefined;

    // The map of the metrics to be highlighted. This allow the parent container to specify which metrics lines need to be highlighted.
    readonly highlightedMetrics?: {
        readonly numericMetrics?: {
            [metricId: string]: boolean | undefined;
        } | undefined;
    } | undefined;

    readonly onNumericMetricSeriesClicked?: (metricId: string, date: number)=> void | undefined;
}



/**
 * This is the component the display chart for numeric and tag metrics. It will display them as 1 chart so that they can be synchronized.
 */
export const SynchronizedNumericTagMetricsDisplayContainer: React.FC<SynchronizedNumericTagMetricsDisplayContainerProps> = (
    props: SynchronizedNumericTagMetricsDisplayContainerProps,
) => {

    const numericChartRef = useRef<am4charts.XYChart>();
    const ganttChartRef = useRef<am4charts.XYChart>();

    const numericMetricsRef = useRef<readonly ChartMetricInfo[]>();
    const tagMetricsRef = useRef<readonly ChartMetricInfo[]>();

    // To keep track of the highlightedMetrics props so that we can detect when it has been modified and recreate series and y axes.
    const highlightedMetricsRef = useRef<SynchronizedNumericTagMetricsDisplayContainerProps['highlightedMetrics']>();

    const numericChartVerticalLineRef = useRef<am4core.Line>();
    const ganttChartVerticalLineRef = useRef<am4core.Line>();

    const classes = useStyle();


    // Get the chart color from the theme. If colors are not defined, use the default predefined set of colors
    const theme: ModeTheme = useTheme();
    const chartCompColors = useMemo(() => {
        if (theme && theme.palette.chart && theme.palette.chart.series.length > 0) {
            return {
                chartColors: theme.palette.chart.series.map((colorSettings): ChartColorSettings => {
                    return {
                        color       : am4core.color(colorSettings.color),
                        contrastText: colorSettings.contrastText ? am4core.color(colorSettings.contrastText) : undefined,
                    };
                }),
                gridStroke  : am4core.color(theme.palette.chart.gridStroke),
                cursorStroke: am4core.color(theme.palette.chart.cursorStroke),
            };
        }
        return {
            chartColors : chartDefaultColors,
            gridStroke  : chartDefaultGridStroke,
            cursorStroke: chartDefaultCursorStroke,
        };
    }, [theme]);


    /**
     * Get the list of visible numeric and tag metrics
     */
    const visibleNumericMetrics = useMemo(() => {
        const metrics = props.numericMetrics.filter((metric) => {
            return props.disabledNumericMetrics === undefined || !props.disabledNumericMetrics[metric.id];
        });
        return metrics;
    }, [props.disabledNumericMetrics, props.numericMetrics]);


    const visibleTagMetrics = useMemo(() => {
        const metrics = props.tagMetrics.filter((metric) => {
            return props.disabledTagMetrics === undefined || !props.disabledTagMetrics[metric.id];
        });
        return metrics;
    }, [props.disabledTagMetrics, props.tagMetrics]);



    /**
     * Sync all the charts X axes's width so that they align vertically
     */
    const syncAxisWidth = useCallback((numericChart: am4charts.XYChart, ganttChart: am4charts.XYChart) => {

        // For the numeric Chart (line chart), the max width of the Y axes is the sum of the Y axes width because the Y axes are laid out horizontally
        const maxNumericChartWidth = numericChart.yAxes.values.reduce((totalWidth, axis) => {
            if (axis) {
                return totalWidth + axis.measuredWidth;
            }
            return totalWidth;
        }, 0);

        // For the Gantt Chart, the max width of the Y axes is the largest width among the Y axes because the Y axes are laid out vertically/stacked
        const maxGanttChartWidth = ganttChart.yAxes.values.reduce((maxWidth, axis) => {
            return Math.max(maxWidth, axis.measuredWidth);
        }, Number.MIN_SAFE_INTEGER);

        // Now pick the largest width between the numeric chart and Gantt chart
        const maxWidth = Math.max(maxNumericChartWidth, maxGanttChartWidth);

        // Now re-adjust the numericChart and ganttChart's Y axes' width according to the maxWidth

        // Because numeric chart's Y axes are laid out horizontally, to make sure the total width is equal maxWidth, we will just increase the
        // width of the FIRST Y axis to push everything else to the right.
        const diff = maxWidth - maxNumericChartWidth;
        const firstYAxis = numericChart.yAxes.getIndex(0);
        if (firstYAxis) {
            firstYAxis.width = firstYAxis.measuredWidth + diff;
        }

        ganttChart.yAxes.each((axis) => {
            if (axis) {
                axis.width = maxWidth;
            }
        });
    }, []);


    /**
     * Because specifically set the Y Axes' width in the 'syncAxisWidth' function, amcharts WILL NOT set their width automatically.
     * This means when something changed such as some series is added or removed, the Y axes don't get adjusted automatically. Therefore,
     * we will have to unset their width to allow amcharts to automatically adjust their sizes.
     */
    const unsetAxisWidth = useCallback((charts: readonly am4charts.XYChart[]) => {
        charts.forEach((chart) => {
            chart.yAxes.each((axis) => {
                if (axis) {
                    // HACK - axis.width has to be a number, can't be undefined. So we will need to cast it to "any" first and set it to undefined
                    // so that the Typescript compiler doesn't complain. amcharts doesn't have a way to unset the width.
                    (axis as any).width = undefined;
                }
            });
        });
    }, []);



    /**
     * Update the numeric chart's data with the specified data object
     */
    const resetNumericChartData = useCallback((chart: am4charts.XYChart, data: MultiMetricsDataPoint[]) => {
        // Adjust date axis base interval by the data resolution
        const dateAxis = chart.xAxes.getIndex(0) as am4charts.DateAxis;
        if (dateAxis) {
            // Get the resolution of the data
            const resolution = getDataResolution(data);
            dateAxis.baseInterval = {
                timeUnit: resolution.timeUnit,
                count   : resolution.amount,
            };
        }

        chart.data = data;
    }, []);



    /**
     * Update the gantt chart's data with the specified data object
     * NOTE: we can't set chart.data = data because the tag data structure are different. We will need to set the data for each
     * individual series.
     */
    const resetGanttChartData = useCallback((
        chart: am4charts.XYChart,
        data: {
            [metricId: string]: GanttChartDataPoint[] | undefined;
        },
    ) => {
        chart.series.each((series) => {
            const metricId = series.id;
            if (metricId !== undefined) {
                series.data = data[metricId] ?? [];
                series.yAxis.data = data[metricId] ?? [];
            }
        });
    }, []);



    /**
     * This useEffect is used for creating a new Am Chart. Creating a Chart can be very slow because we need to dispose the old chart
     * and all its' components before creating a new one. Therefore, we will keep this useEffect's dependencies small and only have it
     * depends on things that required recreating the chart.
     */
    const syncAxisWidthTimeoutId = useRef<number | undefined>();
    useEffect(() => {
        // Create a XY chart
        const createNewNumericChart = !numericChartRef.current;
        const numericChart = !numericChartRef.current || createNewNumericChart
            ? createNumericChart(
                numericChartRef.current, `numeric-chart-${props.chartId}`, theme, chartCompColors,
                props.chartViewSettings?.numericChartSettings?.cursorBehavior,
            )
            : numericChartRef.current;
        numericChartRef.current = numericChart;

        // Create Gantt chart
        const createNewGanttChart = !ganttChartRef.current;
        const ganttChart = createNewGanttChart
            ? createGanttChart(
                ganttChartRef.current, `gantt-chart-${props.chartId}`, theme, chartCompColors,
                props.chartViewSettings?.tagChartSettings?.cursorBehavior,
            )
            : ganttChartRef.current;
        ganttChartRef.current = ganttChart;


        // Because we create 2 different charts, their X Axes will be independent of each other and won't align. We have to manually align them
        // This work only the first time the chart is created.
        if (numericChart && ganttChart) {
            numericChart.events.on('beforevalidated', () => {
                unsetAxisWidth([numericChart, ganttChart]);
            });
            ganttChart.events.on('beforevalidated', () => {
                unsetAxisWidth([numericChart, ganttChart]);
            });
            /**
             * We listen to the "validated" event on each of the charts so that we can call the "syncAxisWidth" but it doesn't work reliably.
             * Sometimes, it will use the smaller width. Turn out this depends on which chart fires the "validated" event first. If one
             * chart fire the "validated" event while the other one is not done rendering, then we can't compute the max width correctly.
             * The max width will end up being whatever the max width that we can compute at that time and it might not include the second
             * chart's max width since the second chart is not done rendering.
             *
             * I found that it works better that we call `syncAxisWidth` after the SECOND chart is done rendering. However, we don't know which
             * chart will finish first AND there is no guarantee both will fire this event. Sometimes only 1 chart needs to be updated therefore
             * only one will fire this event.
             *
             * So the work around this for now is to NOT call "syncAxisWidth" right away on "validated" event. When this event occur, we will
             * create a timeout to call it a few milliseconds later. When the second chart fire the "validated" event, we will cancel the FIRST
             * timeout and create a new timeout. And in the end, only the second timeout we created will execute. And if only 1 chart will
             * fire the "validated" event, it is ok too. There is no event from the second chart therefore the first timeout created won't be
             * canceled and it will be executed later.
             *
             * This seems to work a lot better for now. I have not seen the Y axes misaligned since this change. However, not sure if slower
             * computers will still experience this issue since waiting 100ms might not be long enough for slower computers. Will need to
             * try and see.
             */
            numericChart.events.on('validated', () => {
                // Cancel whatever the current timeout that is waiting to be executed
                window.clearTimeout(syncAxisWidthTimeoutId.current);

                // Create a new timeout to call the "syncAxisWidth" a new MS later
                syncAxisWidthTimeoutId.current = window.setTimeout(() => {
                    syncAxisWidth(numericChart, ganttChart);
                }, 100) as number;

                // Update the vertical line's height when AFTER the chart re-rendered
                if (numericChartVerticalLineRef.current) {
                    numericChartVerticalLineRef.current.y2 = numericChart.plotContainer.measuredHeight;
                }
            });
            ganttChart.events.on('validated', () => {
                // Cancel whatever the current timeout that is waiting to be executed
                window.clearTimeout(syncAxisWidthTimeoutId.current);

                // Create a new timeout to call the "syncAxisWidth" a new MS later
                syncAxisWidthTimeoutId.current = window.setTimeout(() => {
                    syncAxisWidth(numericChart, ganttChart);
                }, 100) as number;

                // Update the vertical line's height when AFTER the chart re-rendered
                if (ganttChartVerticalLineRef.current) {
                    ganttChartVerticalLineRef.current.y2 = ganttChart.plotContainer.measuredHeight;
                }
            });
        }
    }, [chartCompColors, props.chartId, theme, syncAxisWidth, unsetAxisWidth, props.chartViewSettings]);


    /**
     * This useEffect is used for changing the numericChart's scrollBar when the 'shareYAxis' prop changed.
     */
    useEffect(() => {
        if (numericChartRef.current) {
            const chart = numericChartRef.current;

            // Add vertical and horizontal scrollbars based on the settings
            if (props.chartViewSettings?.numericChartSettings?.showHorizontalControlBar) {
                chart.scrollbarX = new am4core.Scrollbar();
                chart.scrollbarX.parent = chart.bottomAxesContainer;
            } else if (chart.scrollbarX) {
                chart.removeDispose(chart.scrollbarX);
            }

            if (props.chartViewSettings?.numericChartSettings?.showVerticalControlBar) {
                chart.scrollbarY = new am4core.Scrollbar();
                chart.scrollbarY.parent = chart.rightAxesContainer;
            } else if (chart.scrollbarY) {
                chart.removeDispose(chart.scrollbarY);
            }
        }

        if (ganttChartRef.current) {
            const chart = ganttChartRef.current;

            // Add vertical and horizontal scrollbars based on the settings
            if (props.chartViewSettings?.tagChartSettings?.showHorizontalControlBar) {
                chart.scrollbarX = new am4core.Scrollbar();
                chart.scrollbarX.parent = chart.bottomAxesContainer;
            } else if (chart.scrollbarY) {
                chart.removeDispose(chart.scrollbarX);
            }

            // Gantt chart doesn't need vertical scrollbar but if numeric chart has a vertical scrollbar, we will also need to add a vertical
            // scrollbar for this chart so that the charts align but will make the scrollbar hidden
            if (props.chartViewSettings?.numericChartSettings?.showVerticalControlBar) {
                chart.scrollbarY = new am4core.Scrollbar();
                chart.scrollbarY.parent = chart.rightAxesContainer;
                chart.scrollbarY.visible = false;
            } else if (chart.scrollbarY) {
                chart.removeDispose(chart.scrollbarY);
            }
        }
    }, [props.chartViewSettings?.numericChartSettings?.showHorizontalControlBar,
        props.chartViewSettings?.numericChartSettings?.showVerticalControlBar, props.chartViewSettings?.tagChartSettings?.showHorizontalControlBar]);



    /**
     * On locale changed, update the chart's dateFormatter's locale
     * This DOES NOT require creating a new chart
     */
    useEffect(() => {
        if (numericChartRef.current && props.chartSettings.locale) {
            numericChartRef.current.dateFormatter.intlLocales = props.chartSettings.locale;
        }
        if (ganttChartRef.current && props.chartSettings.locale) {
            ganttChartRef.current.dateFormatter.intlLocales = props.chartSettings.locale;
        }
    }, [props.chartSettings.locale]);


    /**
     * This useEffect is used for setting up the Export data option
     * This DOES NOT require creating a new chart
     */
    useEffect(() => {
        if (numericChartRef.current) {
            setUpChartExportSettings(numericChartRef.current, props.chartSettings.exportSettings);
        }
        if (ganttChartRef.current) {
            setUpChartExportSettings(ganttChartRef.current, props.chartSettings.exportSettings);
        }
    }, [props.chartSettings.exportSettings]);


    /**
     * This useEffect is used for creating Y axes and series for each numeric metrics.
     * This can be expensive because it requires disposing all the previous series and creating new one so the caller need to make sure
     * the dependencies for this useEffect is only changed when necessary
     * This DOES NOT require creating a new chart but it does create new series
     */
    useEffect(() => {

        // numericMetrics and tagMetrics arrays change sometime but only the pointer to the array change but the internal items in the array
        // don't actually changed. This will cause unnecessary re-creating Y axes and Series. Therefore, we will try to compare the new
        // metrics with the previous one we have in cache to see if the array content changed before we recreate the y axes and series
        // Also, if the highlightedMetrics changed, we need to recreate the y axes and series as well.
        if (numericChartRef.current && (
            JSON.stringify(visibleNumericMetrics) !== JSON.stringify(numericMetricsRef.current)
            || JSON.stringify(props.highlightedMetrics) !== JSON.stringify(highlightedMetricsRef.current)
        )) {
            const chart = numericChartRef.current;

            // remove and dispose the current series and yAxes
            while (chart.series.length > 0) {
                chart.series.removeIndex(0).dispose();
            }
            while (chart.yAxes.length > 0) {
                chart.yAxes.removeIndex(0).dispose();
            }

            // Create series and Y Axes for EACH numeric metric
            if (visibleNumericMetrics.length > 0) {
                const xAxis = chart.xAxes.getIndex(0) as am4charts.DateAxis;
                const yAxesByGroupName = {};

                // The preferred chart order based on the type. Some of the chart type needs to always be displayed at the bottom layer e.g.
                // Bar charts so that they don't cover the other charts therefore we need to force these charts' order in a specific order
                // to make sure they don't cover one another.
                // These are the only chart types which order matter. For the other charts type, they can always show on top.
                const chartOrderByType = {
                    [XYChartType.STACKED_BAR]: 1,
                    [XYChartType.BAR]        : 2,
                    [XYChartType.AREA]       : 3,
                    [XYChartType.LINE]       : 4,
                };
                visibleNumericMetrics.sort((metric1, metric2) => {
                    const chart1Type = metric1.chartType ?? props.chartViewSettings?.numericChartSettings?.chartType ?? XYChartType.LINE;
                    const chart2Type = metric2.chartType ?? props.chartViewSettings?.numericChartSettings?.chartType ?? XYChartType.LINE;
                    const chart1SorOrder = chartOrderByType[chart1Type] ?? 1000;
                    const chart2SortOrder = chartOrderByType[chart2Type] ?? 1000;
                    return chart2SortOrder - chart1SorOrder;
                }).forEach((metric, index) => {
                    createNumericMetricSeries(
                        chart,
                        xAxis,
                        yAxesByGroupName,
                        metric,
                        Boolean(props.highlightedMetrics?.numericMetrics?.[metric.id]),
                        index,
                        visibleNumericMetrics.length,
                        props.chartViewSettings,
                        theme,
                        chartCompColors,
                        props.onNumericMetricSeriesClicked,
                    );
                });
            }


            // Because we re-created axes and series, we need to set chart.data again to have it re-render
            resetNumericChartData(chart, props.metricsData.metricsDataPoints);
            chart.invalidate();

            // Cache the new metrics
            numericMetricsRef.current = visibleNumericMetrics;
            highlightedMetricsRef.current = props.highlightedMetrics;
        }
    }, [chartCompColors, props.metricsData.metricsDataPoints, visibleNumericMetrics, props.onNumericMetricSeriesClicked, resetNumericChartData, theme,
        props.highlightedMetrics?.numericMetrics, props.highlightedMetrics,
        props.chartViewSettings?.numericChartSettings?.selectedMetricStyle?.width, props.chartViewSettings]);



    /**
     * This useEffect is used for creating Y axes and series for each numeric metrics.
     * This can be expensive because it requires disposing all the previous series and creating new one so the caller need to make sure
     * the dependencies for this useEffect is only changed when necessary
     * This DOES NOT require creating a new chart but it does create new series
     */
    useEffect(() => {

        // numericMetrics and tagMetrics arrays change sometime but only the pointer to the array change but the internal items in the array
        // don't actually changed. This will cause unnecessary re-creating Y axes and Series. Therefore, we will try to compare the new
        // metrics with the previous one we have in cache to see if the array content changed before we recreate the y axes and series
        if (ganttChartRef.current && JSON.stringify(visibleTagMetrics) !== JSON.stringify(tagMetricsRef.current)) {
            const chart = ganttChartRef.current;

            // remove and dispose the current series and yAxes
            while (chart.series.length > 0) {
                chart.series.removeIndex(0).dispose();
            }
            while (chart.yAxes.length > 0) {
                chart.yAxes.removeIndex(0).dispose();
            }


            if (visibleTagMetrics.length > 0) {
                visibleTagMetrics.forEach((metric) => {
                    // Create series and Y Axis
                    createGanttChartSeries(
                        chart,
                        metric,
                        theme,
                    );
                });
            }

            // Because we re-created axes and series, we need to set chart.data again to have it re-render
            resetGanttChartData(chart, props.metricsData.ganttChartDataPoints);
            chart.invalidate();

            // Cache the new metrics
            tagMetricsRef.current = visibleTagMetrics;
        }
    }, [props.chartSettings, props.metricsData.ganttChartDataPoints, visibleTagMetrics, props.texts, resetGanttChartData, theme]);



    /**
     * This useEffect is used for updating the numeric chart's Y Axes and Series's adapters when the adapters changed
     */
    useEffect(() => {
        if (numericChartRef.current) {
            const chart = numericChartRef.current;

            // If custom adapter to render Y axis label is provided then use the adapter. If not, just display the y axis value + unit
            chart.yAxes.each((yAxis) => {
                const metricInfo = visibleNumericMetrics.find((metric) => {
                    return getYAxisId(metric) === yAxis.id;
                });
                if (!metricInfo) {
                    return undefined;
                }

                if (props.chartSettings.getNumericMetricChartYAxisTextLabel) {
                    yAxis.renderer.labels.template.adapter.add('text', (text, target) => {
                        if (props.chartSettings.getNumericMetricChartYAxisTextLabel) {
                            return props.chartSettings.getNumericMetricChartYAxisTextLabel(text, target, metricInfo);
                        }
                        return text;
                    });
                } else if (props.chartSettings.getNumericMetricChartYAxisHTMLLabel) {
                    yAxis.renderer.labels.template.adapter.add('html', (text, target) => {
                        if (props.chartSettings.getNumericMetricChartYAxisHTMLLabel) {
                            return props.chartSettings.getNumericMetricChartYAxisHTMLLabel(text, target, metricInfo);
                        }
                        return text;
                    });
                } else {
                    // Override the category axis's renderer to use the tag metric's displayName instead of dataPoint's metricName.
                    yAxis.renderer.labels.template.adapter.add('text', (text, target) => {
                        if (metricInfo.unit) {
                            return `${target.dataItem.values.value.value} ${metricInfo.unit}`;
                        }
                        return text;
                    });
                }

                return undefined;
            });

            chart.series.each((series, index) => {
                // Add tooltip. If there is an adapter to handle creating tooltip text or html, use the adapter. Else use the default tooltip function
                if (props.chartSettings.getNumericMetricChartTooltipText) {
                    series.adapter.add('tooltipText', (text, target) => {
                        return props.chartSettings.getNumericMetricChartTooltipText?.(
                            text, target, visibleNumericMetrics[index],
                        );
                    });
                } else if (props.chartSettings.getNumericMetricChartTooltipHTML) {
                    series.adapter.add('tooltipHTML', (text, target) => {
                        return props.chartSettings.getNumericMetricChartTooltipHTML?.(
                            text, target, visibleNumericMetrics[index],
                        );
                    });
                } else {
                    series.tooltipText = getNumericChartTooltipFormat(visibleNumericMetrics[index].displayName, visibleNumericMetrics[index].unit);
                }
            });
        }
    }, [props.chartSettings, visibleNumericMetrics]);


    /**
     * This useEffect is used for updating the Gantt chart's Y Axes and Series's adapters when the adapters changed
     */
    useEffect(() => {
        if (ganttChartRef.current) {
            const chart = ganttChartRef.current;
            chart.yAxes.each((yAxis, index) => {
                if (props.chartSettings.getTagMetricChartYAxisTextLabel) {
                    yAxis.renderer.labels.template.adapter.add('text', (text, target) => {
                        if (props.chartSettings.getTagMetricChartYAxisTextLabel) {
                            return props.chartSettings.getTagMetricChartYAxisTextLabel(text, target, visibleTagMetrics[index]);
                        }
                        return text;
                    });
                } else if (props.chartSettings.getTagMetricChartYAxisHTMLLabel) {
                    yAxis.renderer.labels.template.adapter.add('html', (text, target) => {
                        if (props.chartSettings.getTagMetricChartYAxisHTMLLabel) {
                            return props.chartSettings.getTagMetricChartYAxisHTMLLabel(text, target, visibleTagMetrics[index]);
                        }
                        return text;
                    });
                } else {
                    // Override the category axis's renderer to use the tag metric's displayName instead of dataPoint's metricName.
                    yAxis.renderer.labels.template.adapter.add('text', (text, target) => {
                        // Get the data point that triggered this function. If there is a dataPoint, return the dataPoint.displayName.
                        // Else return whatever 'text' is
                        const dataPoint = target.dataItem.dataContext as unknown as GanttChartDataPoint;
                        return dataPoint?.displayName ?? text;
                    });
                }
            });

            chart.series.each((series, index) => {
                // Add tooltip. If there is an adapter to handle creating tooltip text or html, use the adapter. Else use the default tooltip function
                if (props.chartSettings.getTagMetricChartTooltipText) {
                    series.adapter.add('tooltipText', (text, target) => {
                        return props.chartSettings.getTagMetricChartTooltipText?.(text, target as am4charts.ColumnSeries, visibleTagMetrics[index]);
                    });
                } else if (props.chartSettings.getTagMetricChartTooltipHTML) {
                    series.adapter.add('tooltipHTML', (text, target) => {
                        return props.chartSettings.getTagMetricChartTooltipHTML?.(text, target as am4charts.ColumnSeries, visibleTagMetrics[index]);
                    });
                } else {
                    (series as am4charts.ColumnSeries).columns.template.tooltipText = getGanttChartTooltipFormat(props.texts);
                }
            });
        }
    }, [props.chartSettings, props.texts, visibleTagMetrics]);


    /**
     * This useEffect is used for creating/updating the vertical line at the specified showVerticalLineAtTimestamp
     */
    useEffect(() => {

        // Create vertical line ONLY if the specified timestamp is between the dateRange
        const timestamp = props.showVerticalLineAtTimestamp;

        // Calculate the timestamp relative to the dateRange in percent. For example: If dateRange is 0pm - 10pm and timestamp is 5pm
        // then relativeTimestamp will be 50%. If timestamp is 1pm then relativeTimestamp is 10%, etc...
        const relativeTimestamp = timestamp !== undefined
            ? ((timestamp - props.dateRange.start) / (props.dateRange.end - props.dateRange.start)) * 100 : undefined;

        if (numericChartRef.current && relativeTimestamp !== undefined && timestamp !== undefined
            && timestamp >= props.dateRange.start && timestamp <= props.dateRange.end) {
            const chart = numericChartRef.current;

            // Get the Line object from numericChartVerticalLineRef, create a new Line if one does not exist, and move it to location X
            const line = createVerticalLine(chart, numericChartVerticalLineRef.current, props.chartViewSettings?.verticalLineStyle, theme);
            line.x = am4core.percent(relativeTimestamp);
            line.disabled = false;
            numericChartVerticalLineRef.current = line;
        } else if (numericChartVerticalLineRef.current) {
            // If the showVerticalLineAtTimestamp is not specified or out of range, hide the vertical line if it exist
            numericChartVerticalLineRef.current.disabled = true;
        }

        if (ganttChartRef.current && relativeTimestamp !== undefined && timestamp !== undefined
            && timestamp >= props.dateRange.start && timestamp <= props.dateRange.end) {
            const chart = ganttChartRef.current;
            // Get the Line object from ganttChartVerticalLineRef, create a new Line if one does not exist, and move it to location X
            const line = createVerticalLine(chart, ganttChartVerticalLineRef.current, props.chartViewSettings?.verticalLineStyle, theme);
            line.x = am4core.percent(relativeTimestamp);
            line.disabled = false;
            ganttChartVerticalLineRef.current = line;
        } else if (ganttChartVerticalLineRef.current) {
            // If the showVerticalLineAtTimestamp is not specified or out of range, hide the vertical line if it exist
            ganttChartVerticalLineRef.current.disabled = true;
        }
    }, [props.chartViewSettings?.verticalLineStyle, props.dateRange.end, props.dateRange.start, props.showVerticalLineAtTimestamp, theme]);



    /**
     * This useEffect is used for drawing the vertical and horizontal boxes on the chart based on the chartViewSettings
     */
    useEffect(() => {
        if (numericChartRef.current) {

            const chart = numericChartRef.current;

            // Draw vertical boxes
            const dateAxis = chart.xAxes.getIndex(0) as am4charts.DateAxis;

            if (dateAxis && props.chartViewSettings?.numericChartSettings?.xAxisBoxes) {
                // Dispose the existing axisRanges
                while (dateAxis.axisRanges.length > 0) {
                    dateAxis.axisRanges.removeIndex(0).dispose();
                }
                props.chartViewSettings.numericChartSettings.xAxisBoxes.forEach((boxSettings) => {
                    const range = dateAxis.axisRanges.create();
                    range.date = new Date(boxSettings.value);
                    if (boxSettings.endValue) {
                        range.endDate = new Date(boxSettings.endValue);
                    }

                    if (boxSettings.stroke) {
                        range.grid.stroke = am4core.color(boxSettings.stroke.color ?? '#000000');
                        range.grid.strokeWidth = boxSettings.stroke.width ?? 1;
                        range.grid.strokeOpacity = boxSettings.stroke.opacity ?? 1;
                        range.grid.strokeDasharray = boxSettings.stroke.dasharray ?? '';
                    }

                    if (boxSettings.fill) {
                        range.axisFill.fill = am4core.color(boxSettings.fill.color);
                        range.axisFill.fillOpacity = boxSettings.fill.opacity ?? 1;
                    }

                    if (boxSettings.label) {
                        range.label.html = boxSettings.label.text;
                        range.label.fill = am4core.color(boxSettings.label.color ?? '#000000');
                        range.label.strokeWidth = 2;
                        range.label.inside = true;
                        range.label.rotation = -90;
                        range.label.valign = 'bottom';
                        range.label.horizontalCenter = 'left';
                        range.label.verticalCenter = 'bottom';
                    }
                });
            }

            // Draw horizontal boxes
            const valueAxis = chart.yAxes.getIndex(0) as am4charts.ValueAxis;
            if (valueAxis && props.chartViewSettings?.numericChartSettings?.yAxisBoxes) {
                // Dispose the existing axisRanges
                while (valueAxis.axisRanges.length > 0) {
                    valueAxis.axisRanges.removeIndex(0).dispose();
                }
                props.chartViewSettings.numericChartSettings.yAxisBoxes.forEach((boxSettings) => {
                    const range = valueAxis.axisRanges.create();
                    range.value = boxSettings.value;
                    if (boxSettings.endValue) {
                        range.endValue = boxSettings.endValue;
                    }
                    if (boxSettings.stroke) {
                        range.grid.stroke = am4core.color(boxSettings.stroke.color ?? '#000000');
                        range.grid.strokeWidth = boxSettings.stroke.width ?? 1;
                        range.grid.strokeOpacity = boxSettings.stroke.opacity ?? 1;
                        range.grid.strokeDasharray = boxSettings.stroke.dasharray ?? '';
                    }

                    if (boxSettings.fill) {
                        range.axisFill.fill = am4core.color(boxSettings.fill.color);
                        range.axisFill.fillOpacity = boxSettings.fill.opacity ?? 1;
                    }

                    if (boxSettings.label) {
                        range.label.html = boxSettings.label.text;
                        range.label.textAlign = 'middle';
                        range.label.fill = am4core.color(boxSettings.label.color ?? '#000000');
                        range.label.strokeWidth = 2;
                        range.label.inside = true;
                        range.label.verticalCenter = 'bottom';
                    }
                });
            }
        }
    }, [props.chartViewSettings?.numericChartSettings?.xAxisBoxes, props.chartViewSettings?.numericChartSettings?.yAxisBoxes, visibleNumericMetrics]);


    /**
     * This useEffect is used for synchronize the 2 chart's X axes to make sure they show the same date range. We don't want the charts
     * to set the x axes' range automatically based on the data
     */
    useEffect(() => {
        if (numericChartRef.current) {
            const dateAxis = numericChartRef.current.xAxes.getIndex(0) as am4charts.DateAxis;
            if (dateAxis) {
                dateAxis.min = props.dateRange.start;
                dateAxis.max = props.dateRange.end;
            }
        }
        if (ganttChartRef.current) {
            const dateAxis = ganttChartRef.current.xAxes.getIndex(0) as am4charts.DateAxis;
            if (dateAxis) {
                dateAxis.min = props.dateRange.start;
                dateAxis.max = props.dateRange.end;
            }
        }
    }, [props.dateRange.end, props.dateRange.start]);



    /**
     * This useEffect is used for replacing the chart data with new data.
     * This DOES NOT require creating new chart
     */
    useEffect(() => {
        if (numericChartRef.current) {
            resetNumericChartData(numericChartRef.current, props.metricsData.metricsDataPoints);
        }
        if (ganttChartRef.current) {
            resetGanttChartData(ganttChartRef.current, props.metricsData.ganttChartDataPoints);
        }
    }, [props.metricsData.metricsDataPoints, props.metricsData.ganttChartDataPoints, resetGanttChartData, resetNumericChartData]);



    /**
     * Compute the height of the tag chart container based on the visible tag metrics.
     */
    const ganttChartContainerHeight = useMemo(() => {
        // The height of the padding, date axis labels, etc...
        const miscContentHeight = 70;

        // If we need to show horizontal scroll bar, which is 30px, we need to add it to the tag chart's total height
        const horScrollbarHeight = props.chartViewSettings?.tagChartSettings?.showHorizontalControlBar ? 30 : 0;

        // The height of all the tag metric chart bars
        return visibleTagMetrics.length * TAG_CHART_BAR_HEIGHT + horScrollbarHeight + miscContentHeight;
    }, [props.chartViewSettings?.tagChartSettings?.showHorizontalControlBar, visibleTagMetrics.length]);


    return (
        <div className={clsx(props.className, classes.root)}>
            <div
                className={clsx(classes.chart, classes.numericChart, visibleNumericMetrics.length === 0 && 'hidden')}
                style={{
                    ...props.style,
                    ...props.numericChartStyle,
                }}
                id={`numeric-chart-${props.chartId}`}
            />
            <div
                className={clsx(classes.chart, visibleTagMetrics.length === 0 && 'hidden')}
                style={{
                    ...props.style,
                    ...props.tagChartStyle,
                    height: ganttChartContainerHeight,
                }}
                id={`gantt-chart-${props.chartId}`}
            />
        </div>
    );
};
